/**
 * Store pour la gestion des configurations de tâches (crédits, temps)
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';

export const useTaskConfigurationStore = defineStore('taskConfiguration', (() => {

    // State de compatibilité des formats entre les réseaux sociaux
    const formatsBySocialNetwork = ref(null);

    // State de la liste des configurations de tâche
    const taskConfigurations = ref(null);

    /**
     * Retourne la liste des configurations de tâche
     * @return array|null
     */
    const getTaskConfigurationList = computed(() => taskConfigurations.value);

    /**
     * Retourne la configuration de tâche dont l'identifiant est en paramètre
     * @return object|null
     */
    const getTaskConfigurationById = computed(() => {
        return ((id) => {
            const tasks = taskConfigurations.value || [];
            const taskFounded = tasks.find((task) => task.id === id);
            return (taskFounded || null);
        });
    });

    /**
     * Retourne la liste des compatibilités des formats entre réseaux sociaux
     * @return array|null
     */
    const getFormatsBySocialNetwork = computed(() => {
        return formatsBySocialNetwork.value;
    });

    /**
     * Retourne la liste de tous les formats (peu importe le réseau)
     * @return array
     */
    const getFormats = computed(() => {

        const taskConfigurations = getTaskConfigurationList.value || [];

        const formatsByNetwork = getFormatsBySocialNetwork.value || {};
        const formats = [];

        Object.keys(formatsByNetwork).forEach((network) => {
            formatsByNetwork[network].forEach((format) => {
                if(formats.indexOf(format) === -1) {
                    formats.push(format);
                }
            });
        });

        return taskConfigurations.filter((configuration) => (formats.indexOf(configuration.type) !== -1));
    });

    /**
     * Retourne les réseaux compatibles avec le format en paramètre
     * @param object format
     * @return array
     */
    const getSocialNetworksForFormat = ((format) => {
        const type = format.type;

        const networks = [];

        const formatsByNetwork = getFormatsBySocialNetwork.value || {};

        Object.keys(formatsByNetwork).forEach((network) => {
            const networkFormats = formatsByNetwork[network];
            if(networkFormats.indexOf(type) !== -1 && networks.indexOf(network) === -1) {
                networks.push(network);
            }
        });

        return networks;
    });

    /**
     * Retourne la configuration dont type de format est en paramètre
     * @param string formatType
     * @return object|null
     */
    const getConfigurationByFormatType = computed(() => {
        return ((formatType) => {
            const taskConfigurations = getTaskConfigurationList.value || [];
            return (taskConfigurations.find((configuration) => configuration.type === formatType) || null);
        });
    });

    /**
     * Chargement des formats par réseau social
     * return Promise
     */
    const loadFormatsBySocialNetwork = (() => {

        if(getFormatsBySocialNetwork.value !== null) {
            return Promise.resolve();
        }

        const path = '/api/task-formats';

        return axios.get(path).then((response) => {
            if(response && response.data && response.data.success && response.data.formats) {
                formatsBySocialNetwork.value = response.data.formats;
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Chargement de toutes les configurations de tâches
     * @return Promise
     */
    const loadTaskConfigurationList = (() => {

        if(taskConfigurations.value !== null) {
            return Promise.resolve();
        }

        const path = '/api/task-configurations';

        return axios.get(path).then((response) => {
            if(response && response.data && response.data.success && response.data.data) {
                updateTaskConfigurationList(response.data.data);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Mise à jour des données dans le store de la configuration de tâche en paramètre
     * @param object taskConfiguration
     * @return void
     */
    const updateTaskConfiguration = ((taskConfiguration) => {

        const currentList = getTaskConfigurationList.value;
        if(currentList === null) {
            return;
        }

        const currentTasks = getTaskConfigurationList.value;
        const index = currentTasks.findIndex((configurationFromState) => configurationFromState.id === taskConfiguration.id);
        if(index !== -1) {
            getTaskConfigurationList.value[index] = { ...currentTasks[index], ...taskConfiguration };
        } else {
            getTaskConfigurationList.value.push(taskConfiguration);
        }
    });

    /**
     * Met à jour la liste des configurations
     * @param array data Données de la liste
     * @return void
     */
    const updateTaskConfigurationList = ((data) => {

        const currentData = getTaskConfigurationList.value;
        if(currentData === null) {
            taskConfigurations.value = data.items;
        } else {

            const currentConfigurations = taskConfigurations.value;
            const newConfigurations = data.items.filter((item) => ! currentConfigurations.find((current) => current.id === item.id));
            taskConfigurations.value = [ ...currentConfigurations, ...newConfigurations ];

            newConfigurations.forEach((configuration) => {
                updateTaskConfiguration(configuration);
            });
        }

    });

    return {
        getTaskConfigurationList,
        getTaskConfigurationById,
        getConfigurationByFormatType,
        getFormatsBySocialNetwork,
        getFormats,
        getSocialNetworksForFormat,
        /***/
        loadTaskConfigurationList,
        loadFormatsBySocialNetwork,
        /***/
        updateTaskConfiguration
    };

}));
